"use client"

import { useEffect, useRef } from "react"

/* ───────────────────────────────────────────────────────────────────────────
   Aperture-Light shared tokens, helpers and keyframes.
   Imported by every section so the design system stays in one place.
   ─────────────────────────────────────────────────────────────────────────── */

export const T = {
  bg:        "#FAF7EE",
  bgSoft:    "#F4F1E8",
  bgInk:     "#0A0A0A",
  surface:   "#FFFFFF",
  surface2:  "#FBF8F0",
  hair:      "rgba(10,10,10,0.08)",
  hair2:     "rgba(10,10,10,0.14)",
  ink:       "#0A0A0A",
  ink2:      "rgba(10,10,10,0.62)",     // ~5.5:1 on cream — passes WCAG AA
  // ink3 was 0.42 — that's ~2.8:1 on cream and Lighthouse / axe flag
  // it as a contrast violation for non-decorative text. Bumped to
  // 0.55 (~4.5:1) so it passes AA without losing the muted feel.
  // Decorative-only uses (em-dashes, eyebrows that are purely
  // ornamental) can opt down to ink4 explicitly.
  ink3:      "rgba(10,10,10,0.55)",
  ink4:      "rgba(10,10,10,0.28)",     // decorative-only — fails AA by design
  acc:       "#0F5132",
  accDeep:   "#0A3D26",
  accLite:   "#1F7A4E",
  accChip:   "#DFF5E5",
  accGlow:   "rgba(15,81,50,0.18)",
  accSoft:   "rgba(15,81,50,0.06)",
  warn:      "#B85C00",
  warnSoft:  "rgba(184,92,0,0.10)",
  bad:       "#A02B1F",
  badSoft:   "rgba(160,43,31,0.10)",
  blue:      "#2D4A8A",
  blueSoft:  "rgba(45,74,138,0.10)",
}

export const FB = "Inter, ui-sans-serif, system-ui, sans-serif"
export const FM = "'JetBrains Mono', ui-monospace, monospace"
export const MAXW = "max-w-[1240px]"

/* Single <style> block injected once per page through ApertureStyles.
   We render it at the top of any aperture section; CSS dedupes on browser. */
export function ApertureStyles() {
  return (
    <style>{`
      @keyframes apl-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.45 } }
      @keyframes apl-marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }
      @keyframes apl-shimmer-x {
        0%   { transform: translateX(-120%) }
        60%  { transform: translateX(120%)  }
        100% { transform: translateX(120%)  }
      }
      @keyframes apl-scan-y {
        0%   { transform: translateY(-100%); opacity: 0 }
        10%  { opacity: 1 }
        90%  { opacity: 1 }
        100% { transform: translateY(2200%); opacity: 0 }
      }
      @keyframes apl-blink {
        0%,90%,100% { opacity: 1 }
        92%,96%     { opacity: 0.25 }
      }
      @keyframes apl-ring-pulse {
        0%   { transform: scale(0.95); opacity: 0.55 }
        70%  { transform: scale(1.4);  opacity: 0    }
        100% { transform: scale(1.4);  opacity: 0    }
      }
      @keyframes apl-rise-card {
        from { opacity: 0; transform: translateY(14px) }
        to   { opacity: 1; transform: translateY(0) }
      }
      @keyframes apl-counter-flash {
        0%,100% { color: ${T.acc} }
        50%     { color: #34D399 }
      }
      .apl-card-hover { transition: transform 320ms cubic-bezier(.2,.8,.2,1), border-color 320ms ease, box-shadow 320ms ease; }
      .apl-card-hover:hover {
        transform: translateY(-3px);
        border-color: rgba(10,10,10,0.18);
        box-shadow: 0 22px 50px -28px rgba(10,10,10,0.18), 0 0 36px -16px ${T.accGlow};
      }
    `}</style>
  )
}

/* RevealGrid — adds a light entrance animation when cards enter the
   viewport. Content stays visible by default; animation must never
   turn sections into blank holes in full-page screenshots, print
   views, slow browsers, or reduced-JS contexts. */
export function RevealGrid({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const root = ref.current
    if (!root) return
    const items = Array.from(root.querySelectorAll<HTMLElement>("[data-reveal]"))
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const el = e.target as HTMLElement
            el.style.animation = `apl-rise-card 600ms cubic-bezier(.2,.8,.2,1) ${el.style.animationDelay || "0ms"} both`
            io.unobserve(el)
          }
        })
      },
      { threshold: 0.15 },
    )
    items.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])
  return <div ref={ref} className={className}>{children}</div>
}

export function SectionEyebrow({ children, center }: { children: React.ReactNode; center?: boolean }) {
  return (
    <p
      className={center ? "text-center" : ""}
      style={{
        fontFamily: FM, fontSize: 11, letterSpacing: "0.18em",
        textTransform: "uppercase", color: T.ink3, fontWeight: 600,
      }}
    >
      <span style={{ color: T.acc, marginRight: 8 }}>—</span>
      {children}
    </p>
  )
}

export function ArrowR({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function Check() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3L13 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
}
export function Dash() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 8h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
}
export function Half() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" /><path d="M8 2 a6 6 0 0 1 0 12 z" fill="currentColor" /></svg>
}
